
import { Helmet } from "react-helmet";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Brain, Cpu, Zap, Bot, Database, Code2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const DeepseekAI = () => {
  return (
    <>
      <Helmet>
        <title>DeepSeek AI - Advanced Open-Source Language Models & AI Solutions</title>
        <meta name="description" content="Explore DeepSeek AI's cutting-edge open-source language models and artificial intelligence solutions. Learn about our cost-effective technology, capabilities, and applications." />
        <meta name="keywords" content="DeepSeek AI, open-source AI, language models, AI revolution, DeepSeek-V3, MoE architecture" />
      </Helmet>

      <div className="min-h-screen pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl">
              <span className="block">DeepSeek AI</span>
              <span className="block text-deepseek-primary mt-2">Democratizing Advanced AI Technology</span>
            </h1>
            <p className="mt-4 text-xl text-gray-500">
              Pushing the boundaries of artificial intelligence with cost-effective, open-source language models and innovative solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Brain className="h-8 w-8 text-deepseek-primary mb-4" />
                <CardTitle>Advanced Language Models</CardTitle>
                <CardDescription>State-of-the-art natural language processing capabilities</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Our models understand context, generate human-like text, and handle complex language tasks with unprecedented accuracy at a fraction of the cost.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Cpu className="h-8 w-8 text-deepseek-primary mb-4" />
                <CardTitle>Cost-Efficient Computing</CardTitle>
                <CardDescription>Optimized architectures for maximum performance</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Built with innovative MoE architecture and Multi-Head Latent Attention to deliver state-of-the-art results at dramatically lower computing costs.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Zap className="h-8 w-8 text-deepseek-primary mb-4" />
                <CardTitle>Open-Source Advantage</CardTitle>
                <CardDescription>Transparent, accessible, and collaborative</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Our MIT-licensed models allow developers worldwide to adapt, improve, and build upon our technology, accelerating AI innovation.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="prose prose-lg mx-auto mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">Our Revolutionary Technology</h2>
            <p className="text-gray-600 mb-6">
              DeepSeek AI leverages advanced neural architectures and innovative training methodologies to create powerful language models at a fraction of the cost of competitors. Our flagship DeepSeek-V3 model costs approximately US$6 million to train, compared to the US$100+ million spent by competitors like OpenAI on models like GPT-4.
            </p>
            <p className="text-gray-600 mb-6">
              We focus on developing AI solutions that are not only powerful but also practical, accessible, and open. Our technology can be integrated into various applications, from content generation to code completion, making advanced AI capabilities available to developers and businesses worldwide without breaking the bank.
            </p>
            <p className="text-gray-600">
              Through continuous research and development, we're constantly pushing the boundaries of what's possible with AI, while maintaining our commitment to openness, cost-efficiency, and ethical considerations in artificial intelligence.
            </p>
          </div>

          <div className="text-center mb-16">
            <Link to="/what-is-deepseek-ai">
              <Button className="bg-deepseek-primary hover:bg-deepseek-primary/90 text-white px-8 py-3 rounded-full text-lg font-medium inline-flex items-center">
                Learn More About DeepSeek AI
                <Bot className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
            <Card>
              <CardHeader>
                <Database className="h-8 w-8 text-deepseek-primary mb-4" />
                <CardTitle>Open-Source Philosophy</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  DeepSeek embraces an open-source philosophy by releasing its models with open weights under the MIT license. This means anyone can download, use, and modify our model code, fostering collaboration and rapid innovation.
                </p>
                <p className="text-gray-600">
                  This approach democratizes access to high-performance language models, allowing developers worldwide to experiment with and improve upon our technology.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Code2 className="h-8 w-8 text-deepseek-primary mb-4" />
                <CardTitle>Innovative Architecture</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Our models utilize cutting-edge techniques like Mixture-of-Experts (MoE), which selectively activates only a subset of parameters for each token, dramatically reducing computational demands while maintaining performance.
                </p>
                <p className="text-gray-600">
                  This efficiency-focused approach delivers state-of-the-art AI capabilities at a fraction of the traditional cost, making advanced AI more accessible than ever.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeepseekAI;
